import PictureImage from '../Common/PictureImage';
import React, { Suspense } from 'react';
import { Link } from 'react-router-dom';
import Canonical from '../../seo/Canonical';
import '../ShopifyFulfilment/ShopifyFulfilment.css';
import './Logistics.css';
import Newsletter from '../Homepage/Newsletter';
import Footer from '../Layouts/Footer';
import Testimonials from '../Homepage/Testimonials';
import FAQs from '../Homepage/FAQs';
import '../Homepage/FAQ.css';
import { useRevealClass } from '../../hooks/useInViewOnce';
/* ─────────────────────────────────────────────────────────────────────
   Constants
───────────────────────────────────────────────────────────────────── */
const FREIGHT_HERO_IMAGE =
  '/logisticsimages/freightforwarder.webp';

/* ─────────────────────────────────────────────────────────────────────
   One-time keyframe + hover CSS injection
   (keeps the file self-contained without touching Logistics.css)
───────────────────────────────────────────────────────────────────── */
const STYLE_ID = 'ff-uk-injected-styles';
if (typeof document !== 'undefined' && !document.getElementById(STYLE_ID)) {
  const s = document.createElement('style');
  s.id = STYLE_ID;
  s.textContent = `
    @keyframes ffFadeUp   { from{opacity:0;transform:translateY(24px)} to{opacity:1;transform:translateY(0)} }
    @keyframes ffSlideIn  { from{opacity:0;transform:translateX(-16px)} to{opacity:1;transform:translateX(0)} }

    .ff-ind-card  { border-top:3px solid transparent; transition:border-color .25s,transform .25s,box-shadow .25s; }
    .ff-ind-card:hover { border-top-color:#1e3a8a; transform:translateY(-4px); box-shadow:0 12px 36px rgba(30,58,138,.12); }

    .ff-stat-card { transition:background .25s,transform .25s; }
    .ff-stat-card:hover { background:rgba(255,255,255,.1)!important; transform:translateY(-4px); }

    .ff-carrier-card { transition:border-color .22s,box-shadow .22s,transform .22s; }
    .ff-carrier-card:hover { border-color:#1e3a8a!important; box-shadow:0 6px 24px rgba(30,58,138,.1); transform:translateY(-2px); }

    .ff-testimonial-card { transition:box-shadow .25s,transform .25s; position:relative; }
    .ff-testimonial-card:hover { box-shadow:0 12px 40px rgba(30,58,138,.1); transform:translateY(-4px); }
    .ff-testimonial-card::before {
      content:'\u201C'; position:absolute; top:20px; right:28px;
      font-size:5rem; line-height:1; color:#e2e8f0;
      font-family:Georgia,serif; pointer-events:none;
    }

    .ff-ind-visible   .ff-ind-card        { animation:ffFadeUp  .5s  ease both; }
    .ff-stats-visible .ff-stat-card       { animation:ffFadeUp  .55s ease both; }
    .ff-cars-visible  .ff-carrier-card    { animation:ffSlideIn .45s ease both; }
    .ff-testi-visible .ff-testimonial-card{ animation:ffFadeUp  .55s ease both; }

    @media(max-width:768px){
      .ff-ind-grid,.ff-stats-grid,.ff-carriers-grid,.ff-testi-grid{
        grid-template-columns:1fr!important;
      }
    }
  `;
  document.head.appendChild(s);
}

/* ─────────────────────────────────────────────────────────────────────
   Component
───────────────────────────────────────────────────────────────────── */
const FreightForwarderUK: React.FC = () => {

  /* reveal refs */
  const servicesRef     = useRevealClass('logistics-services--visible');
  const whyRef          = useRevealClass('logistics-why--visible');
  const industriesRef   = useRevealClass('ff-ind-visible');

  /* ── DATA ── */
  const freightServices: { id: number; title: string; copy: React.ReactNode }[] = [
    { id: 1, title: 'Inbound Freight Collection', copy: 'We collect goods from UK and EU suppliers with flexible FTL and LTL freight forwarding solutions.' },
    { id: 2, title: 'Pallet Distribution',        copy: 'Reliable pallet distribution across the UK with tracked next-day and economy delivery options for businesses.' },
    {
      id: 3,
      title: 'Amazon FBA Deliveries',
      copy: (
        <>
          Scheduled{' '}
          <Link to="/amazon-fba-shipments">Amazon FBA deliveries</Link> with compliant documentation,
          pallet labelling, and fulfilment centre coordination across the UK.
        </>
      ),
    },
    { id: 4, title: 'B2B Wholesale Delivery',      copy: 'Professional wholesale deliveries to retailers, distribution centres, and trade locations with flexible unloading options.' },
  ];

  const workflowSteps: { id: number; title: string; desc: React.ReactNode }[] = [
    { id: 1, title: 'Collection',        desc: 'We collect inventory directly from UK, EU, and international suppliers.' },
    { id: 2, title: 'Customs Clearance', desc: 'Our team manages customs documentation and imports compliance for smooth border clearance.' },
    {
      id: 3,
      title: 'Secure Warehouse Receiving',
      desc: (
        <>
          Every shipment is barcode scanned, inspected, and logged into our{' '}
          <Link to="/wms-integrations">warehouse management system</Link>.
        </>
      ),
    },
    {
      id: 4,
      title: 'Final Delivery',
      desc: (
        <>
          Goods are dispatched to Amazon fulfilment centres, retailers, or via{' '}
          <Link to="/multi-channel-fulfillment-services">multi-channel fulfilment</Link> to your customers across the UK.
        </>
      ),
    },
  ];

  const industries: { icon: string; title: string; copy: React.ReactNode }[] = [
    {
      icon: '🛒',
      title: 'Ecommerce & Direct-to-Consumer',
      copy: (
        <>
          Integrated freight forwarding and fulfilment for{' '}
          <Link to="/shopify-fulfillment-services">Shopify</Link>, Amazon,{' '}
          <Link to="/tiktok-shop-fulfillment-services">TikTok Shop</Link>, and ecommerce brands.
        </>
      ),
    },
    { icon: '🏭', title: 'Manufacturing',       copy: 'Reliable factory collections, inventory receiving, quality inspections, and warehouse storage for manufacturers.' },
    { icon: '💊', title: 'Health & Beauty',     copy: 'Careful handling and secure storage for cosmetics, personal care, and health products.' },
    { icon: '👗', title: 'Fashion & Apparel',   copy: 'Efficient garment logistics for retailers, ecommerce brands, and direct-to-consumer fulfilment.' },
    { icon: '🔧', title: 'Industrial & Tools',  copy: 'Specialist handling for heavy, oversized, and palletised freight with secure warehouse receiving.' },
    { icon: '📦', title: 'Subscription Boxes', copy: 'Coordinated inbound freight and fulfilment for recurring subscription box deliveries and product launches.' },
  ];

  /* ── RENDER ── */
  return (
    <div className="logistics-page">
      <Canonical baseUrl="https://www.pickpackpro.co.uk" />
      {/* ════════════════════════════════════════
          HERO
      ════════════════════════════════════════ */}
      <section className="hero-section fba-page-hero logistics-hero" aria-label="Freight forwarder UK">
        <PictureImage
          src={FREIGHT_HERO_IMAGE}
          alt="UK freight forwarding logistics services for supply chain"
          className="fba-page-hero__bg logistics-hero-img"
          width={1920}
          height={1080}
          decoding="async"
          isHero
        />
        <div className="fba-page-hero__overlay" aria-hidden />
        <div className="fba-page-hero__content">
          <div className="sh-left">
            <h1 className="sh-main-title logistics-hero-title">
              <span className="logistics-hero-title-light">Freight Forwarder UK for </span>{' '}
              <span className="logistics-hero-title-accent">Ecommerce, Amazon FBA & Retail Businesses</span>
            </h1>
            <div className="sh-desc logistics-hero-desc">
              <p>
                Our freight forwarding services simplify international shipping, inbound logistics, and customs
                coordination for UK businesses. We collect goods from UK and EU suppliers, transport them to our{' '}
                <Link to="/fulfilment-centre-milton-keynes">Milton Keynes warehouse</Link>, and seamlessly
                integrate freight forwarding with{' '}
                <Link to="/ecommerce-fulfillment-services">ecommerce fulfilment</Link>,{' '}
                <Link to="/amazon-fba-prep-services">Amazon FBA preparation</Link>, and order dispatch.
              </p>
            </div>
            <div className="sh-cta-wrapper">
              <Link to="/contact-fulfillment-services" className="sh-cta-btn">
                Get a Free Freight Quote
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════
          NEW International Shipping Intro - After Hero
      ════════════════════════════════════════ */}
      <section className="logistics-page-section freight-intro-section" aria-labelledby="freight-intro-title">
        <div className="logistics-page-section-inner">
          <header className="logistics-page-section-header">
            <h2 id="freight-intro-title" className="logistics-page-section-title">
             International Freight Forwarding Services from the UK
            </h2>
            <p className="logistics-page-section-lead">
              We provide reliable freight forwarding services, helping businesses move goods from global suppliers
              to our{' '}
              <Link to="/our-fulfilment-center">UK fulfilment centre</Link> quickly, securely, and ready for{' '}
              <Link to="/blog/pick-and-pack-fulfilment-uk-explained">pick and pack fulfilment</Link>. Read our{' '}
              <Link to="/blog/3pl-logistics-uk-explained-2026">3PL logistics UK guide</Link> for how inbound
              freight fits a modern seller supply chain.
            </p>
          </header>

          <div className="logistics-page-block">
            <h3 className="logistics-page-subtitle">Air Freight</h3>
            <div className="logistics-media-card">
              <PictureImage
                src="/Images/Air.webp"
                alt="Air freight airplane"
                className="logistics-media-card__img"
                loading="lazy"
                decoding="async"
              />
              <div className="logistics-media-card__overlay">
                <h4 className="logistics-media-card__title">
                  {/* Fast Air <span className="logistics-media-card__title-accent">Freight Worldwide</span> */}
                </h4>
                <p className="logistics-media-card__copy">
    Express air freight solutions for time-sensitive shipments, combining fast transit, customs clearance, and seamless warehouse receiving in the UK.
                </p>
              </div>
            </div>
          </div>

          <div className="logistics-page-block">
            <h3 className="logistics-page-subtitle">Sea Freight</h3>
            <div className="logistics-media-card">
              <PictureImage
                src="/logisticsimages/Sea Freight.webp"
                alt="Sea freight shipping"
                className="logistics-media-card__img"
                loading="lazy"
                decoding="async"
              />
              <div className="logistics-media-card__overlay">
                {/* <h4 className="logistics-media-card__title">Reliable Sea Freight Solutions</h4> */}
                <p className="logistics-media-card__copy">
 Reliable sea freight services offering cost-effective FCL and LCL shipping with customs support and UK warehouse delivery.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════
          EXISTING 1 — Services Grid
      ════════════════════════════════════════ */}
      <section ref={servicesRef} className="logistics-services" aria-labelledby="freight-services-title">
        <div className="logistics-services-inner">
          <header className="logistics-services-header">
            <p className="logistics-services-eyebrow">What we handle</p>
            <h2 id="freight-services-title" className="logistics-services-title">Our Freight Services</h2>
           <p>
              Our freight forwarding services cover every stage of inbound logistics, from supplier collection to
              warehouse receiving,{' '}
              <Link to="/amazon-fba-shipments">Amazon FBA deliveries</Link>, and B2B distribution — alongside our
              broader <Link to="/logistics-services">logistics services hub</Link> and{' '}
              <Link to="/b2b-fulfillment">B2B fulfilment</Link>.
            </p>
          </header>
          <div className="logistics-tiles-grid" role="list">
            {freightServices.map((s) => (
              <article key={s.id} className="logistics-tile logistics-tile-animate" role="listitem">
                <h3 className="logistics-tile-title">{s.title}</h3>
                <p className="logistics-tile-copy">{s.copy}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════
          EXISTING 2 — Workflow Steps
      ════════════════════════════════════════ */}
      <section className="logistics-page-section logistics-workflow-section">
        <div className="logistics-page-section-inner">
          <header className="logistics-page-section-header">
            {/* <p className="logistics-services-eyebrow">Seamless Integration</p> */}
            <h2 className="logistics-services-title">How Our Freight Workflow Works</h2>
            <p>
              Our streamlined freight forwarding process ensures your goods move efficiently from supplier
              collection to warehouse receiving,{' '}
              <Link to="/blog/how-to-prepare-amazon-inventory">Amazon inventory prep</Link>, fulfilment, and final
              delivery.
            </p>
          </header>
          <div className="logistics-workflow-grid">
            {workflowSteps.map((step) => (
              <div key={step.id}>
                <div className="logistics-workflow-step-num">{step.id}</div>
                <h3 className="logistics-workflow-step-title">{step.title}</h3>
                <p className="logistics-workflow-step-copy">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════
          EXISTING 3 — Why Choose Us
      ════════════════════════════════════════ */}
      <section ref={whyRef} className="logistics-why">
        <div className="logistics-why-inner">
          <div className="logistics-why-top">
            <div className="logistics-why-copy">
              <p className="logistics-why-eyebrow">Why Pick Pack Pro</p>
              <h2 className="logistics-why-title">Why use Pick Pack Pro for logistics?</h2>
              <p className="logistics-why-intro">
                Pick Pack Pro offers one integrated solution for freight forwarding, warehouse management, and{' '}
                <Link to="/shopify-fulfillment-services">Shopify fulfilment</Link>. We collect goods from global
                suppliers, manage customs and inbound logistics, barcode every shipment, and deliver directly to
                Amazon fulfilment centres or customers — see{' '}
                <Link to="/blog/how-to-outsource-ecommerce-fulfilment">how to outsource ecommerce fulfilment</Link>{' '}
                when you are ready to scale with reliable{' '}
                <Link to="/ecommerce-fulfillment-services">ecommerce fulfilment services</Link> or our{' '}
                <Link to="/logistics-services">logistics services hub</Link>.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════
          NEW 1 — Industries We Serve
      ════════════════════════════════════════════════════════ */}
      <section
        ref={industriesRef as React.RefObject<HTMLElement>}
        aria-labelledby="freight-industries-title"
        style={{ padding: '100px 0', background: '#f8fafc' }}
      >
        <div className="container">
          <header style={{ textAlign: 'center', marginBottom: '60px' }}>
            {/* <p className="logistics-services-eyebrow">Sectors we support</p> */}
            <h2 id="freight-industries-title" className="logistics-services-title">
             Industries We Support with Freight Forwarding & Fulfilment
            </h2>
            {/* ← global p */}
            <p style={{ maxWidth: '580px', margin: '0.75rem auto', padding: 0, fontSize: '1rem', color: '#64748b', lineHeight: 1.65 }}>
         Our freight forwarding and fulfilment services support businesses across multiple industries, delivering reliable inbound logistics, inventory management, and nationwide distribution.
            </p>
          </header>
          <div
            className="ff-ind-grid"
            role="list"
            style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '24px' }}
          >
            {industries.map((ind, i) => (
              <article
                key={i}
                role="listitem"
                className="ff-ind-card"
                style={{
                  background: '#fff',
                  border: '1px solid #e2e8f0',
                  borderRadius: '14px',
                  padding: '32px 28px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '14px',
                  animationDelay: `${i * 80}ms`,
                }}
              >
                <span style={{ fontSize: '2rem', lineHeight: 1 }} aria-hidden="true">{ind.icon}</span>

                {/* ← global h4 */}
                <h3 style={{ color: '#0f172a', fontSize: '1.1rem', fontWeight: 700, margin: '0.5rem 0', padding: 0 }}>{ind.title}</h3>

                {/* ← global p */}
                <p style={{ color: '#64748b', fontSize: '1rem', lineHeight: 1.65, margin: '0.75rem 0', padding: 0 }}>{ind.copy}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Homepage Google Reviews - Exact replica */}
      <Testimonials
        title="Trusted by Businesses Across the UK"
        intro="Pick Pack Pro is proud to maintain a 5.0-star Google rating based on verified customer reviews. Businesses trust our freight forwarding, warehouse logistics, and fulfilment services for reliable deliveries, responsive support, and efficient supply chain management that helps keep inventory moving and customers satisfied."
      />

      {/* ════════════════════════════════════════
          FAQ - Exactly like Homepage (using FAQSection)
      ════════════════════════════════════════ */}
      <Suspense fallback={null}>
        <FAQs
          title="Freight Forwarder UK FAQs"
          description="Answers to common questions about freight forwarding, international shipping, customs clearance, warehouse receiving, and inbound logistics for UK businesses."
          faqs={[
            {
              id: 1,
              question: 'Do you provide both air and sea freight options for UK imports?',
              answer:
                'Yes. We can arrange air freight for time-sensitive shipments and sea freight for cost-effective volume. We handle end-to-end coordination including booking, documentation support and integration with your warehouse receiving in the UK.'
            },
            {
              id: 2,
              question: 'How do you handle customs clearance for shipments coming into the UK?',
              answer:
                'We support the customs process with experienced documentation handling and compliance checks. You share the required shipper details and product information; we help ensure the paperwork is prepared accurately so your goods can move smoothly into the UK.'
            },
            {
              id: 3,
              question: 'Can you consolidate freight from multiple suppliers into one delivery to our warehouse?',
              answer:
                'Yes. If you have multiple suppliers, we can coordinate collection and consolidation so your inbound goods arrive together for easier receiving. This reduces complexity for you and supports faster downstream pick-and-pack once stock is booked in.'
            },
            {
              id: 4,
              question: 'Do you offer pallet distribution and next-day delivery options after freight arrives?',
              answer:
                'After receiving at our Milton Keynes facility, we can support pallet distribution across the UK with carrier options that suit your delivery needs. For many routes we can offer next-day coverage depending on service level and destination.'
            },
            {
              id: 5,
              question: 'Do you support Amazon FC deliveries and handle labelling/documentation?',
              answer:
                'Yes. We can arrange scheduled deliveries to Amazon Fulfilment Centres and help with documentation and labelling requirements as part of the inbound flow, so your inventory is ready for the next stage of fulfilment.'
            },
            {
              id: 6,
              question: 'How quickly can we start—what is the onboarding process?',
              answer:
                'Onboarding is straightforward: we confirm your shipment profile and requirements, agree on a bespoke quote, then set up the operational steps for receiving and onward fulfilment. Most clients can be ready to go live within 2–5 business days depending on the complexity of their setup.'
            },
          ]}
        />
      </Suspense>

      <Newsletter />
      <Footer />
    </div>
  );
};

export default FreightForwarderUK;
